/**
 * What the browser remembers about being signed in.
 *
 * **A real sign-in keeps its session in an httpOnly cookie** (#360, spec "The web"): the sign-in
 * service sets `__Secure-better-auth.session_token` (httpOnly, Secure, SameSite=Lax) on
 * groundwork-sports.com, the browser sends it by itself, and no script on the page can read it.
 * So nothing here holds a token for a real sign-in, and the old `#token=` hand-off is gone: it
 * used to put the token in the URL fragment and then in `localStorage`, where any script could
 * read it.
 *
 * Three things are stored, and none is a credential:
 *
 *  - `groundwork_signed_in` ("the hint"): this browser has a sign-in session. It exists because the
 *    cookie is invisible to the page, and the app's gate (`Layout`) must answer "is there a
 *    session at all" before the first paint without a request. It is written only after the
 *    server confirmed the cookie (`SignedIn.tsx`, `Login.tsx`) and removed with the session; if it
 *    is stale (the session expired or was revoked elsewhere) the first API call answers 401 and
 *    `redirectToLogin()` clears it. Nothing is authorised by it.
 *  - `groundwork_last_used_provider` ("Last used", SI-D4/SI-D5): which provider this device signed in
 *    with last. The device's, not the account's: signing out keeps it, and it is written on a
 *    successful sign-in and by nothing else.
 *  - **Developer Mode's token** (`groundwork_token`, with `groundwork_dev_session`): a local stack
 *    cannot complete Sign in with Apple, so `api/scripts/dev_login_link.py` prints a link whose
 *    fragment carries a session token and `dev=1`, and the "Continue as developer" button mints a
 *    legacy one. Both are stored and sent as a bearer, and the persistent "Dev session" badge
 *    follows the marker, so a local run can never be taken for production. **Only a fragment that
 *    says `dev=1` is accepted**: a plain `#token=` is a hand-off for a real sign-in, which no longer
 *    exists, and is dropped.
 */

import { signOutAnalytics } from '../analytics'

const TOKEN_KEY = 'groundwork_token'

/**
 * Marks the stored session as a Developer Mode token. The UI shows a
 * persistent marker while it is set — mistaking a local session for a
 * production one is how a local test gets reported as a production
 * verification.
 */
const DEV_SESSION_KEY = 'groundwork_dev_session'

const SIGNED_IN_KEY = 'groundwork_signed_in'
const LAST_USED_KEY = 'groundwork_last_used_provider'

export type Provider = 'apple' | 'google'

export function isDevSession(): boolean {
  return localStorage.getItem(DEV_SESSION_KEY) === 'true'
}

export function setDevSession(): void {
  localStorage.setItem(DEV_SESSION_KEY, 'true')
}

/** A Developer Mode (or legacy) bearer token, or null. A real sign-in has none. */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

/** True when this browser believes it has a session: a Developer Mode token, or the hint. */
export function hasSession(): boolean {
  return getToken() !== null || localStorage.getItem(SIGNED_IN_KEY) === 'true'
}

/** The server confirmed the cookie: remember that this browser has a session. */
export function markSignedIn(): void {
  localStorage.setItem(SIGNED_IN_KEY, 'true')
}

/** Forgets the session (the token, the Developer Mode marker, the hint). Keeps "Last used": it is the device's. */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(DEV_SESSION_KEY)
  localStorage.removeItem(SIGNED_IN_KEY)
}

/** The provider this device signed in with last, or null when it has no history. */
export function getLastUsedProvider(): Provider | null {
  try {
    const value = localStorage.getItem(LAST_USED_KEY)
    return value === 'apple' || value === 'google' ? value : null
  } catch {
    return null
  }
}

/** Written on a successful sign-in and by nothing else (SI-D5). */
export function setLastUsedProvider(provider: Provider): void {
  try {
    localStorage.setItem(LAST_USED_KEY, provider)
  } catch {
    // A browser that refuses storage just shows no chip.
  }
}

const ATTEMPT_KEY = 'groundwork_signing_in_with'

/**
 * Notes which provider this tab is about to sign in with, in this tab's `sessionStorage`, which
 * survives the round trip to Apple and back. `SignedIn.tsx` reads it once the server has
 * confirmed the session, so "Last used" is written on a successful sign-in and not on a tap.
 */
export function noteSignInAttempt(provider: Provider): void {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, provider)
  } catch {
    // Without it the chip simply is not written.
  }
}

/** The provider noted by `noteSignInAttempt`, removed as it is read. */
export function takeSignInAttempt(): Provider | null {
  try {
    const value = sessionStorage.getItem(ATTEMPT_KEY)
    sessionStorage.removeItem(ATTEMPT_KEY)
    return value === 'apple' || value === 'google' ? value : null
  } catch {
    return null
  }
}

/** Auth header for fetch calls — empty object when no Developer Mode token exists (a cookie needs none). */
export function authHeaders(): Record<string, string> {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Capture a Developer Mode link's token from the URL fragment. Called once at app boot,
 * before anything renders or queries fire.
 */
export function consumeTokenFromUrl(): void {
  // `[^&]+` rather than `.+`: api/scripts/dev_login_link.py appends `&dev=1`,
  // and a greedy match would swallow it into the token. The token is the sign-in
  // service's signed `token.signature` (letters, digits, a dot, and base64 characters
  // that may include + / = or their percent-encoded forms) or a legacy one
  // (`secrets.token_urlsafe` output). Neither contains `&`, so it is unambiguous.
  const match = window.location.hash.match(/^#token=([^&]+)(?:&(.*))?$/)
  if (!match) return
  // Only a Developer Mode link is a hand-off now. A plain fragment token would be the old
  // sign-in's, which no longer exists; it is dropped, never stored.
  if (new URLSearchParams(match[2] ?? '').get('dev') === '1') {
    setToken(match[1])
    setDevSession()
  }
  history.replaceState(null, '', window.location.pathname + window.location.search)
}

/**
 * Session is invalid/expired — drop it and show the login screen.
 * No-op when already on /login so a failing probe can't loop.
 */
export function redirectToLogin(): void {
  clearToken()
  if (window.location.pathname !== '/login') {
    // The session ended under this page (#708): the analytics module is told as `signOut()` tells it, so Google's
    // cookies and the person's reference go before the page is left. Without it the next person on a shared computer,
    // with a `g` cookie, would load the container with the last one's client id. The exit below is already a full page
    // load, which is what the module asks of a page that has the container. Not on `/login` itself: nothing is left.
    signOutAnalytics()
    window.location.assign('/login')
  }
}
